import React, { useState, useEffect } from "react";
import { Link } from "react-router";
import * as _ from "lodash";
import { TrackingTags } from "shared/src/tracking/Tracking";

type Props = {
    description: string | undefined;
    pdpLink: string | null;
    eventId: string;
    bettyBundleId: string | undefined;
    imageUrl: string | undefined;
    variant: string[] | undefined;
    fallbackImageUrl: string | undefined;
    onArticleOpen: (
        clickedElement: string,
        eventId: string,
        bettyBundleId: string | null | undefined,
        optionalTrackingTags?: Partial<TrackingTags>
    ) => void;
    onSuccessfulImageLoad: (
        eventId: string,
        description: string | undefined,
        imageType: string,
        bettyBundleId: string | undefined
    ) => void;
    optionalTrackingTags?: Partial<TrackingTags>;
};

type UrlObject = {
    url: string;
    urlType: string;
};

export function buildUrls(
    fallbackImageUrl: string | null | undefined,
    variant: string[] | null | undefined,
    imageUrl: string | null | undefined
): UrlObject[] {
    const urls = [];
    if (imageUrl)
        urls.push({
            url: imageUrl,
            urlType: "bundle"
        });
    if (variant) urls.push(...variant.map((variantUrl) => ({ url: variantUrl, urlType: "variant" })));
    if (fallbackImageUrl)
        urls.push({
            url: fallbackImageUrl,
            urlType: "bundleFallback"
        });
    urls.push({
        url: "/ordercapture/uidispatcher/static/app/assets/images/placeholder_food_item.png",
        urlType: "fallback"
    });
    return _.uniqBy(urls, "url");
}

function appendWidthAndHeightQueryParams(url: string, width: number, height: number) {
    const prefix = url.includes("?") ? "&" : "?";
    return url + prefix + "w=" + width + "&h=" + height;
}

function computeNextIdx(counter: number, urlsLen: number) {
    return counter >= 0 && counter + 1 < urlsLen ? counter + 1 : urlsLen - 1;
}

export default function ImageDisplay({
    description,
    pdpLink,
    eventId,
    bettyBundleId,
    imageUrl,
    variant,
    fallbackImageUrl,
    onArticleOpen,
    onSuccessfulImageLoad,
    optionalTrackingTags
}: Props) {
    const imgSize = 144;
    const urls = buildUrls(fallbackImageUrl, variant, imageUrl);
    const [counter, setCounter] = useState(0);
    useEffect(() => setCounter(0), [bettyBundleId]);
    const renderImage = () => {
        return (
            <img
                src={appendWidthAndHeightQueryParams(urls[counter].url, 144, 144)}
                loading="lazy"
                alt={description}
                onError={() => setCounter(computeNextIdx(counter, urls.length))}
                onLoad={() => onSuccessfulImageLoad(eventId, description, urls[counter].urlType, bettyBundleId)}
                height={imgSize}
                width={imgSize}
            />
        );
    };

    if (!pdpLink) return renderImage();

    return (
        <Link
            className="image"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore  -- Note: The description is not a real property of Links, but something we just pass on anyways
            description={description}
            to={pdpLink}
            onClick={() => onArticleOpen("image", eventId, bettyBundleId, optionalTrackingTags)}>
            {renderImage()}
        </Link>
    );
}
